// src/pages/CheckIn.jsx
import { saveToR2 } from '@/api/r2';
import React, { useState, useEffect, useCallback } from "react";
import { DailyCheckIn, User, Lesson, UserProgress } from "@/api/entities";
import { ArrowLeft, Heart, Target, MessageCircle, Wind, BrainCircuit, Users, Trash2, Home, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion, AnimatePresence } from "framer-motion";

// ---------- helpers numerici/tempo/debug ----------
const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const clamp  = (x, min, max) => Math.min(Math.max(num(x, 0), min), max);
const round2 = (x) => Math.round((num(x, 0) + Number.EPSILON) * 100) / 100;
const browserTZ = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
};
const dbg = (...a) => {
  if (localStorage.getItem('rb_debug') === '1') console.log('[checkin]', ...a);
};

// identità robusta per fallback/POST di emergenza
const getIdentity = (user) => {
  const id    = user?.id    || localStorage.getItem('rb_user_id')    || 'anon';
  const email = user?.email || localStorage.getItem('rb_user_email') || null;
  const name  = user?.full_name || null;
  return { id, email, name };
};

// ---- MODELLO DI CONSUMO (sigarette vs tabacco) ----
function computeSmokingModel(user) {
  const N = (x, d=0) => { const n = Number(x); return Number.isFinite(n) ? n : d; };
  const type = user?.smoking_type || 'cigarettes';

  if (type === 'tobacco') {
    // 1 pacco tabacco = 100 "sigarette" equivalenti
    const CIGS_PER_TOBACCO_PACK = 100;

    const tpw = N(user?.tobacco_packs_per_week);
    const tpp = N(user?.tobacco_pack_price);
    const ppw = N(user?.paper_packs_per_week);
    const ppp = N(user?.paper_pack_price);
    const fpw = N(user?.filter_packs_per_week);
    const fpp = N(user?.filter_pack_price);

    const weeklyCigs  = tpw * CIGS_PER_TOBACCO_PACK;
    const baselineCpd = weeklyCigs / 7;
    const weeklyCost  = tpw*tpp + ppw*ppp + fpw*fpp;
    const dailyCost   = weeklyCost / 7;
    const pricePerCig = baselineCpd > 0 ? dailyCost / baselineCpd : 0;

    return { type, baselineCpd, pricePerCig, dailyCost };
  }

  // Sigarette "classiche"
  const baselineCpd = N(
    user?.baseline_cigs_per_day,
    N(localStorage.getItem('rb_baseline_cpd'),
      N(user?.cigarettes_per_day, 20)
    )
  );
  const packPrice   = N(user?.pack_price_eur ?? user?.pack_price, N(localStorage.getItem('rb_pack_price_eur'), 6));
  const cigsPerPack = N(user?.cigs_per_pack ?? user?.cigarettes_per_pack, N(localStorage.getItem('rb_cigs_per_pack'), 20));
  const pricePerCig = cigsPerPack > 0 ? packPrice / cigsPerPack : 0;
  const dailyCost   = pricePerCig * baselineCpd;

  return { type: 'cigarettes', baselineCpd, pricePerCig, dailyCost };
}

const AssistantResponse = ({ message, onAcknowledged }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="bg-blue-50 border border-blue-200 p-4 rounded-xl mb-6 text-center"
  >
    <p className="text-blue-800 font-medium">{message}</p>
    {onAcknowledged && (
      <Button onClick={onAcknowledged} size="sm" className="mt-3">
        Ok, ho capito
      </Button>
    )}
  </motion.div>
);

export default function CheckIn() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    q1_feeling: [7],
    q1_problem_if_low: "",
    q2_motivation: [7],
    q2_problem_if_low: "",
    q3_breathing: "",
    relapse_cigarettes_smoked: "",
    q4_helped: "",
    q4_helped_other_text: "",
    q5_difficulty: "",
    q5_difficulty_other_text: "",
  });
  const [assistantMessage, setAssistantMessage] = useState("");
  const [showLowScoreInput, setShowLowScoreInput] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [startTime] = useState(Date.now()); // per completion_time_seconds

  // ---------- bootstrap ----------
  const loadInitialData = useCallback(async () => {
    try {
      const currentUser = await User.me();
      setUser(currentUser);

      // ▶︎ PERSIST IDENTITÀ (serve alla /progress come fallback)
      if (currentUser?.email) localStorage.setItem('rb_user_email', currentUser.email);
      if (currentUser?.id)    localStorage.setItem('rb_user_id', currentUser.id);
      // helper di debug/interop
      window.__driveGetUser = () => ({
        id: currentUser?.id || localStorage.getItem('rb_user_id') || 'anon',
        email: currentUser?.email || localStorage.getItem('rb_user_email') || null,
        full_name: currentUser?.full_name || null,
      });

      // salva in locale i baseline se presenti (coerenza future sessioni)
      if (currentUser?.baseline_cigs_per_day) {
        localStorage.setItem('rb_baseline_cpd', String(currentUser.baseline_cigs_per_day));
      }
      if (currentUser?.pack_price_eur) {
        localStorage.setItem('rb_pack_price_eur', String(currentUser.pack_price_eur));
      }
      if (currentUser?.cigs_per_pack) {
        localStorage.setItem('rb_cigs_per_pack', String(currentUser.cigs_per_pack));
      }

      // evita doppio check-in nello stesso giorno
      const today = new Date().toISOString().split('T')[0];
      const checkIns = await DailyCheckIn.filter({ created_by: currentUser.email, date: today });
      const completedCheckIn = checkIns.find(c =>
        c.q1_feeling && c.q2_motivation && c.q3_breathing
      );
      if (completedCheckIn) {
        navigate(createPageUrl("Dashboard"));
      }
    } catch (error) {
      console.error("Errore caricamento dati:", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const userName = user?.full_name?.split(' ')[0] || "Utente";

  const resetLesson6Progress = async () => {
    try {
      const currentUser = await User.me();
      const lesson6Arr = await Lesson.filter({ order: 6 });
      if (lesson6Arr.length > 0) {
        const lesson6Id = lesson6Arr[0].id;
        const progressArr = await UserProgress.filter({ created_by: currentUser.email, lesson_id: lesson6Id });
        if (progressArr.length > 0 && progressArr[0].exercise_completed) {
          await UserProgress.update(progressArr[0].id, { exercise_completed: false });
          console.log("Progresso esercizio 6 resettato a causa di una ricaduta.");
        }
      }
    } catch (error) {
      console.error("Errore reset progresso lezione 6:", error);
    }
  };

  // ---------- flusso step ----------
  const handleNextStep = (step, value) => {
    let message = "";
    if (step === 1) {
      if (value >= 7) message = `Bene ${userName}, siamo fieri di te, continua così!`;
      else if (value >= 6) message = `È normale ${userName} avere qualche difficoltà all'inizio, più passa il tempo più sarà facile.`;
      else { setShowLowScoreInput('feeling'); return; }
    } else if (step === 2) {
      if (value >= 7) message = `Bene ${userName}, siamo fieri di te, continua così!`;
      else if (value >= 6) message = `È normale ${userName} avere qualche difficoltà all'inizio, più passa il tempo più sarà facile.`;
      else { setShowLowScoreInput('motivation'); return; }
    } else if (step === 3) {
      if (value === 'relapse') resetLesson6Progress();
      const breathingMap = {
        yoga_relax: `Bene ${userName}, siamo fieri di te, continua così!`,
        rested: `Ok ${userName}, domani cerca di mettercela tutta.`,
        relapse: `Tranquillo ${userName}, può capitare a tutti un passo falso. Ora guarda subito il modulo delle ricadute e fai gli esercizi, ricordati che puoi farcela!`
      };
      message = breathingMap[value];
    }
    setAssistantMessage(message);
    setTimeout(() => { setAssistantMessage(""); setCurrentStep(currentStep + 1); }, message.length > 80 ? 4000 : 3000);
  };

  const handleLowScoreSubmit = () => {
    const message = `Tieni duro ${userName}, ricordati che continuare adesso è una delle scelte migliori che puoi fare per la tua vita.`;
    setShowLowScoreInput(null);
    setAssistantMessage(message);
    setTimeout(() => { setAssistantMessage(""); setCurrentStep(currentStep + 1); }, 4000);
  };

  // ---------- invio finale ----------
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const finalData = {
        ...formData,
        date: today,
        q1_feeling:     num(formData.q1_feeling?.[0] ?? formData.q1_feeling ?? 0, 0),
        q2_motivation:  num(formData.q2_motivation?.[0] ?? formData.q2_motivation ?? 0, 0),
        relapse_cigarettes_smoked: formData.q3_breathing === 'relapse'
          ? num(formData.relapse_cigarettes_smoked, 0)
          : null,
      };

      // Validazione ricaduta (0..baseline)
      const baselineFromUser = num(user?.baseline_cigs_per_day, null);
      const baselineFromLS   = num(localStorage.getItem('rb_baseline_cpd'), null);
      const baselineCpdTmp   = baselineFromUser ?? baselineFromLS ?? 20;

      if (finalData.q3_breathing === 'relapse') {
        if (finalData.relapse_cigarettes_smoked === null || finalData.relapse_cigarettes_smoked === "" || finalData.relapse_cigarettes_smoked < 0) {
          setIsSubmitting(false);
          setAssistantMessage("Per favore indica quante sigarette hai fumato nella ricaduta (0 o più).");
          setTimeout(() => setAssistantMessage(""), 4000);
          return;
        }
        finalData.relapse_cigarettes_smoked = clamp(finalData.relapse_cigarettes_smoked, 0, baselineCpdTmp);
      }

      // === 3) Calcolo progressi giornalieri (supporto tabacco + cartine + filtri) ===
      const model = computeSmokingModel(user);
      const baselineCpd = model.baselineCpd;   // “sigarette equivalenti”/giorno
      const pricePerCig = model.pricePerCig;   // € per “sigaretta” equivalente

      // persisti baseline in locale (per coerenza UI)
      localStorage.setItem('rb_baseline_cpd', String(Math.round(baselineCpd)));

      const relapses = finalData.q3_breathing === 'relapse' ? 1 : 0;
      const smoked   = relapses ? clamp(finalData.relapse_cigarettes_smoked, 0, baselineCpd) : 0;

      const cigarettesAvoided = Math.max(0, baselineCpd - smoked);
      const savedToday  = round2(pricePerCig * cigarettesAvoided);
      const status      = relapses ? 'relapse' : 'success';

      const identity   = getIdentity(user);
      const createdBy  = identity.email || identity.id || 'anon';

      // === RECORD COMPLETO per lo store locale (usato dalla /progress) ===
      const record = {
        ...finalData,
        created_by: createdBy,
        created_at: new Date().toISOString(),
        status,
        // campi espliciti
        cigarettes_avoided: cigarettesAvoided,
        savings_eur: savedToday,
        // alias legacy per massima compatibilità con componenti esistenti
        cigs_avoided: cigarettesAvoided,
        eur: savedToday,
      };

      dbg('progress snapshot', {
        modelType: model.type,
        baselineCpd, pricePerCig, relapses, smoked,
        cigarettesAvoided, savedToday, today, createdBy
      });

      // 1) Salvataggio locale (storico)
      await DailyCheckIn.create(record);

      // 2) Invio evento grezzo del check-in (helper + fallback)
      const dailyPayload = {
        data: {
          ...record,
          user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
          completion_time_seconds: Math.floor((Date.now() - startTime) / 1000),
        },
        meta: { source: 'checkin_page', tz: browserTZ() },
      };
      try {
        await saveToR2('daily_checkin', dailyPayload);
      } catch (err) {
        console.warn('[R2] daily_checkin via helper fallito, provo fallback diretto', err);
        try {
          await fetch('/api/drive', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ kind: 'daily_checkin', user: identity, ...dailyPayload }),
          });
        } catch (e2) {
          console.error('[R2] Fallback daily_checkin fallito:', e2);
        }
      }

      // 4) Invio snapshot (helper + fallback)
      const statsPayload = {
        data: {
          date: today,
          relapses,
          relapse_cigarettes_smoked: smoked,
          cigarettes_avoided: cigarettesAvoided,
          saved_today: savedToday,
          lastCheckin: today,
          achieved_goals: [],
          next_goals: [],
        },
        meta: { source: 'checkin_page', tz: browserTZ() },
      };

      try {
        await saveToR2('user_stats_progress', statsPayload);
      } catch (e) {
        console.warn('[R2] user_stats_progress via helper fallito, provo fallback diretto', e);
        try {
          await fetch('/api/drive', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ kind: 'user_stats_progress', user: identity, ...statsPayload }),
          });
        } catch (e2) {
          console.error('[R2] Fallback user_stats_progress fallito:', e2);
        }
      }

      // notifica /progress per il refresh immediato
      window.dispatchEvent(new Event('rb:checkin-saved'));

      setAssistantMessage(`Grazie per il feedback, ${userName}!`);
      setTimeout(() => navigate(createPageUrl("Dashboard")), 1500);
    } catch (error) {
      console.error("Errore invio check-in:", error);
      setIsSubmitting(false);
    }
  };

  // ---------- opzioni UI ----------
  const q4Options = [
    { value: "respiro", label: "Respiro", icon: Wind },
    { value: "movimento", label: "Movimento", icon: Users },
    { value: "ambiente", label: "Ambiente", icon: Home },
    { value: "social", label: "Social", icon: Users },
    { value: "mindset", label: "Mindset", icon: BrainCircuit },
    { value: "motivazione", label: "Motivazione", icon: Sparkles },
    { value: "distrazione", label: "Distogliere l'attenzione", icon: Sparkles },
    { value: "altro", label: "Altro (specifica)", icon: MessageCircle },
  ];
  // 🔧 AGGIUNTO "Niente, ho tutto sotto controllo" + correzione etichette
  const q5Options = [
    { value: "niente", label: "Niente, ho tutto sotto controllo", icon: Sparkles },
    { value: "impulso", label: "Impulso/desiderio momentaneo", icon: Sparkles },
    { value: "abitudini", label: "Eliminare abitudini associate", icon: Trash2 },
    { value: "circolo_sociale", label: "Il mio circolo sociale", icon: Users },
    { value: "giudizio", label: "Approvazione/giudizio degli altri", icon: Users },
    { value: "pulizia_ambiente", label: "Pulizia ambiente intorno a me", icon: Home },
    { value: "altro", label: "Altro (specifica)", icon: MessageCircle },
  ];

  // ---------- render step ----------
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="shadow-xl bg-gradient-to-br from-pink-50 to-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl text-pink-700">
                <Heart className="w-6 h-6" />Come ti senti oggi?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Non bene</span>
                <span className="text-2xl font-bold text-pink-600">{formData.q1_feeling[0]}/10</span>
                <span>Alla grande!</span>
              </div>
              <Slider value={formData.q1_feeling} onValueChange={(v) => setFormData(p => ({ ...p, q1_feeling: v }))} max={10} min={1} step={1} className="my-4" />
              <Button onClick={() => handleNextStep(1, formData.q1_feeling[0])} className="w-full">Avanti</Button>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card className="shadow-xl bg-gradient-to-br from-blue-50 to-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl text-blue-700">
                <Target className="w-6 h-6" />Quanto ti senti motivato?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Poco</span>
                <span className="text-2xl font-bold text-blue-600">{formData.q2_motivation[0]}/10</span>
                <span>Moltissimo!</span>
              </div>
              <Slider value={formData.q2_motivation} onValueChange={(v) => setFormData(p => ({ ...p, q2_motivation: v }))} max={10} min={1} step={1} className="my-4" />
              <Button onClick={() => handleNextStep(2, formData.q2_motivation[0])} className="w-full">Avanti</Button>
            </CardContent>
          </Card>
        );
      case 3:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Wind className="w-6 h-6 text-cyan-600" />Oggi hai respirato bene?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={formData.q3_breathing} onValueChange={(v) => { setFormData(p => ({ ...p, q3_breathing: v })); }}>
                <div className="flex items-center space-x-2"><RadioGroupItem value="yoga_relax" id="r1" /><Label htmlFor="r1">Sì, ho fatto yoga o un esercizio di rilassamento</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="rested" id="r2" /><Label htmlFor="r2">Oggi mi sono riposato</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="relapse" id="r3" /><Label htmlFor="r3">Oggi ho avuto una ricaduta</Label></div>
              </RadioGroup>

              <AnimatePresence>
                {formData.q3_breathing === 'relapse' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-4 space-y-2 overflow-hidden">
                    <Label htmlFor="relapse_cigarettes">Quante sigarette hai fumato? (0–{baselineFromUser ?? baselineFromLS ?? 20})</Label>
                    <Input
                      id="relapse_cigarettes"
                      type="number"
                      value={formData.relapse_cigarettes_smoked}
                      onChange={e => setFormData(p => ({ ...p, relapse_cigarettes_smoked: e.target.value }))}
                      placeholder="Es. 5"
                      min="0"
                      max={baselineFromUser ?? baselineFromLS ?? 20}
                      step="1"
                      className="mt-1"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {formData.q3_breathing && (
                <Button onClick={() => handleNextStep(3, formData.q3_breathing)} className="w-full mt-4">
                  Conferma e Avanti
                </Button>
              )}
            </CardContent>
          </Card>
        );
      case 4:
        return (
          <Card className="shadow-xl">
            <CardHeader><CardTitle>Cosa ti ha aiutato di più oggi?</CardTitle></CardHeader>
            <CardContent>
              <RadioGroup value={formData.q4_helped} onValueChange={(v) => setFormData(p => ({ ...p, q4_helped: v }))}>
                {[
                  { value: "respiro", label: "Respiro", icon: Wind },
                  { value: "movimento", label: "Movimento", icon: Users },
                  { value: "ambiente", label: "Ambiente", icon: Home },
                  { value: "social", label: "Social", icon: Users },
                  { value: "mindset", label: "Mindset", icon: BrainCircuit },
                  { value: "motivazione", label: "Motivazione", icon: Sparkles },
                  { value: "distrazione", label: "Distogliere l'attenzione", icon: Sparkles },
                  { value: "altro", label: "Altro (specifica)", icon: MessageCircle },
                ].map(o => (
                  <div key={o.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={o.value} id={`q4-${o.value}`} />
                    <Label htmlFor={`q4-${o.value}`}>{o.label}</Label>
                  </div>
                ))}
              </RadioGroup>
              {formData.q4_helped === 'altro' && (
                <Textarea value={formData.q4_helped_other_text} onChange={e => setFormData(p => ({ ...p, q4_helped_other_text: e.target.value }))} placeholder="Descrivi..." className="mt-4" />
              )}
              <Button onClick={() => setCurrentStep(5)} className="w-full mt-4">Avanti</Button>
            </CardContent>
          </Card>
        );
      case 5:
        return (
          <Card className="shadow-xl">
            <CardHeader><CardTitle>Cosa ti ha dato più difficoltà?</CardTitle></CardHeader>
            <CardContent>
              <RadioGroup value={formData.q5_difficulty} onValueChange={(v) => setFormData(p => ({ ...p, q5_difficulty: v }))}>
                {[
                  // 🔧 nuovo ordine + testi corretti
                  { value: "niente", label: "Niente, ho tutto sotto controllo", icon: Sparkles },
                  { value: "impulso", label: "Impulso/desiderio momentaneo", icon: Sparkles },
                  { value: "abitudini", label: "Eliminare abitudini associate", icon: Trash2 },
                  { value: "circolo_sociale", label: "Il mio circolo sociale", icon: Users },
                  { value: "giudizio", label: "Approvazione/giudizio degli altri", icon: Users },
                  { value: "pulizia_ambiente", label: "Pulizia ambiente intorno a me", icon: Home },
                  { value: "altro", label: "Altro (specifica)", icon: MessageCircle },
                ].map(o => (
                  <div key={o.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={o.value} id={`q5-${o.value}`} />
                    <Label htmlFor={`q5-${o.value}`}>{o.label}</Label>
                  </div>
                ))}
              </RadioGroup>
              {formData.q5_difficulty === 'altro' && (
                <Textarea value={formData.q5_difficulty_other_text} onChange={e => setFormData(p => ({ ...p, q5_difficulty_other_text: e.target.value }))} placeholder="Descrivi..." className="mt-4" />
              )}
              <Button onClick={handleFinalSubmit} disabled={isSubmitting} className="w-full mt-4 bg-green-600 hover:bg-green-700">
                {isSubmitting ? "Invio..." : "Completa Check-in"}
              </Button>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  // baseline per mostrare il max dell'input durante lo step 3
  const baselineFromUser = num(user?.baseline_cigs_per_day, null);
  const baselineFromLS   = num(localStorage.getItem('rb_baseline_cpd'), null);

  const renderLowScoreInput = () => {
    if (!showLowScoreInput) return null;
    const isFeeling = showLowScoreInput === 'feeling';
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <Card className="max-w-sm w-full">
          <CardHeader><CardTitle>Qual è il problema?</CardTitle></CardHeader>
        <CardContent>
            <Textarea
              value={isFeeling ? formData.q1_problem_if_low : formData.q2_problem_if_low}
              onChange={e => setFormData(p => ({
                ...p,
                [isFeeling ? 'q1_problem_if_low' : 'q2_problem_if_low']: e.target.value
              }))}
              placeholder="Descrivi cosa non va..."
            />
            <Button onClick={handleLowScoreSubmit} className="w-full mt-4">Invia</Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Button variant="outline" onClick={() => navigate(createPageUrl("Dashboard"))} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Bacheca
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Check-in <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">Giornaliero</span>
          </h1>
          <p className="text-lg text-gray-600">Prenditi un momento per riflettere sul tuo percorso oggi</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {assistantMessage && <AssistantResponse message={assistantMessage} />}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div key={currentStep} initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -30, opacity: 0 }} transition={{ duration: 0.3 }}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {renderLowScoreInput()}
      </div>
    </div>
  );
}
